import { QueryClientProvider } from '@tanstack/react-query';
import {
  STALE_TIMES,
  GC_TIMES,
  createAppQueryClient,
} from '@ais-ui/custom-hooks';
import React, { Suspense } from 'react';
import * as ReactDOM from 'react-dom/client';
import { AuthProvider } from '@ais-ui/auth';
import type { Session } from '@ais-ui/auth';
import { checkSessionValid, getOverlayMP } from '@ais-ui/websockets';
import { ToasterWithViewState, toast } from '@ais-ui/ui-core';
import { HashRouter as Router, useLocation } from 'react-router-dom';
import { App } from './app/app';

import {
  LocalizationProvider,
  useLocalization,
} from '@ais-ui/locale-management';
import { translations } from './locales/translations';
import { ConfigContextProvider } from './configuration/configContext';
import { ErrorBoundary } from '@ais-ui/error-boundaries';
import { ProfileProvider } from '@ais-ui/profile';
import { useFavicon } from './configuration/useFavicon';
import {
  useSentry,
  initSentry,
  startMemoryDiagnostics,
} from './configuration/useSentry';
import { useApplicationTitle } from './configuration/useApplicationTitle';
import { useTheme } from './configuration/useTheme';
import { useServiceWorker } from './configuration/useServiceWorker';
import { useAppVersionCheck } from './hooks/useAppVersionCheck';
import { ROUTES } from './routes';
import { fetchConfig, DynamicConfigurations } from './configuration/config';
import { setRuntimeSettingsPhase } from '@ais-ui/user-settings';

const initializeMapView = async (session: Session) => {
  await getOverlayMP(
    session,
    {
      centerLat: 0.0001,
      centerLon: 0.0001,
      zoomLevel: 18,
      width: 1,
      height: 1,
      leftLon: 0.0001,
      rightLon: 0.0002,
    },
    'UTC',
  );
};

// Lazy load PopupApp - popups are separate windows that don't need the full bundle
const PopupApp = React.lazy(() =>
  import('./modules/Popup/PopupApp').then((m) => ({ default: m.PopupApp })),
);

// Minimal loading state for popup - must be outside lazy chunk
const PopupLoadingState = () => (
  <div className="flex items-center justify-center h-screen bg-background">
    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
  </div>
);

function RootComponent({ config }: { config: DynamicConfigurations }) {
  return (
    // <StrictMode>
    <ConfigContextProvider initialConfig={config}>
      <LocalizationProvider translations={translations}>
        <AppComponent />
      </LocalizationProvider>
    </ConfigContextProvider>
    // </StrictMode>
  );
}

/**
 * Router that handles popup routes separately from main app
 * Popup routes bypass AuthProvider to avoid auto-login redirects
 */
const AppRouter = () => {
  const location = useLocation();
  const { messages } = useLocalization();

  // Handle service worker updates on navigation
  useServiceWorker();

  // Poll for new deployments and show a toast when one is detected
  useAppVersionCheck();

  // Check if this is a popup route - these bypass AuthProvider
  const isPopupRoute = location.pathname.startsWith('/popup');

  if (isPopupRoute) {
    return (
      <ErrorBoundary messages={messages}>
        <Suspense fallback={<PopupLoadingState />}>
          <PopupApp />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Main app with full AuthProvider
  return (
    <ProfileProvider>
      <AuthProvider
        routePaths={{
          mapPath: ROUTES.MAP.$path(),
          mfaBeforeLoginPath: ROUTES.MULTIFACTOR.BEFORE_LOGIN.$path(),
          changePasswordPath: ROUTES.AUTHENTICATION.CHANGE_PASSWORD.$path(),
        }}
        checkSessionValid={checkSessionValid}
        initializeMapView={initializeMapView}
      >
        <ToasterWithViewState>
          <ErrorBoundary messages={messages}>
            <App />
          </ErrorBoundary>
        </ToasterWithViewState>
      </AuthProvider>
    </ProfileProvider>
  );
};

/**
 * Registers the React Router integration with Sentry inside the Router tree.
 * Uses useLayoutEffect so it fires before any useEffect-based navigations
 * (e.g. AuthProvider session restore), ensuring early navigations are tracked.
 */
const SentryRouterIntegration = () => {
  useSentry();
  return null;
};

const AppComponent = () => {
  const { isLoading: isLoadingFavIcon } = useFavicon();
  const { isLoading: isLoadingTitle } = useApplicationTitle();
  const { isLoading: isLoadingTheme } = useTheme();

  if (isLoadingFavIcon || isLoadingTitle || isLoadingTheme) {
    return null;
  }

  return (
    <Router>
      <SentryRouterIntegration />
      <QueryClientProvider client={queryClient}>
        <AppRouter />
      </QueryClientProvider>
    </Router>
  );
};

// Global toast-on-error + 4xx-no-retry defaults come from `createAppQueryClient`
// (see `@ais-ui/custom-hooks/errorHandling`) — only the app-specific
// staleTime/gcTime are overridden here.
const queryClient = createAppQueryClient({
  notify: toast,
  defaultOptions: {
    queries: {
      staleTime: STALE_TIMES.MODERATE,
      gcTime: GC_TIMES.STANDARD,
    },
  },
});

// Bootstrap: fetch config → init Sentry (before page finishes loading) → mount React
async function bootstrap() {
  const config = await fetchConfig();
  setRuntimeSettingsPhase(config.userSettingsPhase);
  initSentry(
    config.dsn,
    config.sentryEnvironment,
    __APP_VERSION__ ?? 'unknown',
  );

  // Deliberately not inside initSentry: its console readout is what the
  // reproduction steps use, and an environment without a DSN needs it too.
  startMemoryDiagnostics(queryClient.getQueryCache());

  const rootElement = document.getElementById('root') as HTMLElement;
  rootElement.className = 'isolate';
  const root = ReactDOM.createRoot(rootElement);
  root.render(<RootComponent config={config} />);
}

bootstrap().catch((error) => {
  console.error('Failed to start application:', error);
  const root = document.getElementById('root');
  if (root) {
    root.innerHTML =
      '<div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif">' +
      '<div style="text-align:center"><h1>Unable to load application</h1>' +
      '<p>Please check your connection and try refreshing the page.</p></div></div>';
  }
});
